import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CompanionCard from "@/components/CompanionCard";
import { Reveal, MaskedLines, EASE_LUXE } from "@/components/Reveal";

export default function AvailableNow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) {
      navigate("/login");
      return;
    }
    if (!user) return;
    api.get("/companions?available_now=true&sort=featured")
      .then((r) => setCompanions(r.data))
      .catch(() => setCompanions([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <div className="min-h-screen pt-28 md:pt-32" data-testid="available-now-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        {/* Hero */}
        <div className="relative overflow-hidden border border-velvet-rose/20 bg-gradient-to-br from-velvet-rose/10 via-velvet-ink to-velvet-coal p-8 md:p-14">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: EASE_LUXE }}
            className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-velvet-rose/25 blur-3xl"
          />
          <div className="relative">
            <div className="mb-6 flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-velvet-rose opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-velvet-rose" />
              </span>
              <p className="text-xs uppercase tracking-luxe text-velvet-rose">Live &middot; Right now</p>
            </div>
            <MaskedLines
              lines={["Available", <span key="n" className="italic text-velvet-rose">Now.</span>]}
              lineClassName="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-8xl"
            />
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/70">
              These companions have flagged themselves ready to meet in the next hour.
              Message them from their profile to lock in the encounter.
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="mt-14">
          {loading ? (
            <div className="py-20 text-center text-sm font-light text-white/40">Finding who&rsquo;s free right now…</div>
          ) : companions.length === 0 ? (
            <div className="border border-white/10 py-24 text-center" data-testid="available-now-empty">
              <Sparkles className="mx-auto mb-4 text-velvet-gold" size={32} />
              <p className="font-heading text-3xl font-light text-white">Nobody live at this moment</p>
              <p className="mt-3 text-sm font-light text-white/50">
                Check back in a few minutes — or explore the full directory.
              </p>
              <Link
                to="/directory"
                data-testid="available-now-browse-all"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-velvet-rose hover:text-black"
              >
                Browse the directory
              </Link>
            </div>
          ) : (
            <>
              <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">
                {companions.length} live &middot; ready now
              </p>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {companions.map((c, i) => (
                  <Reveal key={c.slug} delay={i * 0.05}>
                    <CompanionCard c={c} />
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="h-32" />
      </div>
    </div>
  );
}
